// Line Entry — the criteria step of a General Process source. Shown before the
// lines when the process has criteria the user may set (`line_criteria`
// answers `ask`). Each value arrives pre-filled from the line source's mapping
// (this record's PO number, its date) or the process's own default; a
// PROTECTED one is shown read-only — the engine takes it from the record and
// ignores whatever the browser sends. Styled like the other line-entry
// dialogs (the "Bulk Line Entry UX" canvas).

import { useMemo, useState } from 'react'
import { Lock, X } from 'lucide-react'
import { Z_RECORD_DIALOG } from '../../layers'
import type { LineCriterion } from './contract'
import type { LineCmd } from './useLineEntry'
import { T, btn, btnPri, dialog, dialogFoot, overlay } from './styles'

interface Props {
  cmd: LineCmd
  noun: string
  criteria: LineCriterion[]
  /** The values chosen last time (reopened from the picker). */
  initial?: Record<string, unknown>
  /** The command adds every line without a picker. */
  addsDirectly: boolean
  onCancel: () => void
  /** The user's values for the criteria that are NOT protected. */
  onSubmit: (values: Record<string, unknown>) => void
}

const field: React.CSSProperties = {
  height: 36, border: `1px solid ${T.field}`, borderRadius: 6, padding: '0 10px', fontSize: 14,
  fontFamily: 'inherit', color: T.text, background: T.bg, boxSizing: 'border-box', width: '100%', minWidth: 0,
}

function asText(v: unknown): string {
  if (v == null) return ''
  if (Array.isArray(v)) return v.map(x => String(x ?? '')).join(', ')
  return String(v)
}
function asDate(v: unknown): string {
  const m = asText(v).match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}
function pair(v: unknown): [string, string] {
  if (Array.isArray(v)) return [asText(v[0]), asText(v[1])]
  const parts = asText(v).split(',').map(s => s.trim())
  return [parts[0] ?? '', parts[1] ?? '']
}
function blank(v: unknown): boolean {
  if (v == null || v === '') return true
  if (Array.isArray(v)) return v.every(x => x == null || String(x).trim() === '')
  return false
}

export default function LineCriteria({ cmd, noun, criteria, initial, addsDirectly, onCancel, onSubmit }: Props) {
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {}
    for (const c of criteria) {
      const prev = initial && Object.keys(initial).find(k => k.toLowerCase() === c.name.toLowerCase())
      out[c.name] = !c.protected && prev ? initial![prev] : c.value
    }
    return out
  })
  const [error, setError] = useState<string | null>(null)
  const open = useMemo(() => criteria.filter(c => !c.protected), [criteria])
  const set = (name: string, v: unknown) => { setError(null); setValues(prev => ({ ...prev, [name]: v })) }

  const submit = () => {
    const missing = open.find(c => blank(values[c.name]) && !c.default_helper)
    if (missing) {
      setError(`Enter ${missing.label || missing.name}.`)
      return
    }
    const out: Record<string, unknown> = {}
    for (const c of open) {
      const v = values[c.name]
      if (blank(v)) continue
      if (c.multi === 'list' && !Array.isArray(v)) {
        out[c.name] = asText(v).split(',').map(s => s.trim()).filter(Boolean)
      } else {
        out[c.name] = v
      }
    }
    onSubmit(out)
  }

  const input = (c: LineCriterion) => {
    const v = values[c.name]
    const type = c.data_type === 'date' ? 'date' : c.data_type === 'number' ? 'number' : 'text'
    const label = c.label || c.name
    if (c.protected) {
      return (
        <div style={{ ...field, display: 'flex', alignItems: 'center', gap: 8, background: T.band, color: T.muted }}
          title="Set by this record — it cannot be changed here">
          <Lock size={13} aria-hidden="true" />
          <span style={{ fontFamily: T.mono, color: T.text }}>{asText(v) || '—'}</span>
        </div>
      )
    }
    if (c.multi === 'range') {
      const [a, b] = pair(v)
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <input type={type} aria-label={`${label} from`} style={field}
            value={type === 'date' ? asDate(a) : a} onChange={e => set(c.name, [e.target.value, b])} />
          <span style={{ alignSelf: 'center', color: T.muted, fontSize: 13 }}>to</span>
          <input type={type} aria-label={`${label} to`} style={field}
            value={type === 'date' ? asDate(b) : b} onChange={e => set(c.name, [a, e.target.value])} />
        </div>
      )
    }
    if (c.multi === 'list') {
      return (
        <input type="text" aria-label={label} style={field} placeholder="comma-separated"
          value={asText(v)} onChange={e => set(c.name, e.target.value)} />
      )
    }
    return (
      <input type={type} aria-label={label} style={field}
        value={type === 'date' ? asDate(v) : asText(v)} onChange={e => set(c.name, e.target.value)} />
    )
  }

  return (
    <div role="presentation" onClick={onCancel} style={{ ...overlay, zIndex: Z_RECORD_DIALOG, paddingTop: 60 }}>
      <div role="dialog" aria-modal="true" aria-labelledby="le-crit-title" onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); submit() } }}
        style={{ ...dialog, maxWidth: 640 }}>
        <div style={{ padding: '18px 22px 14px', display: 'flex', flexDirection: 'column', gap: 6,
          borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!addsDirectly && <span style={{ fontSize: 13, color: T.muted, fontWeight: 600 }}>Step 1 of 2</span>}
            <span style={{ flex: 1 }} />
            <button type="button" onClick={onCancel} aria-label="Close" style={{ ...btn, padding: '0 10px' }}>
              <X size={16} />
            </button>
          </div>
          <h2 id="le-crit-title" style={{ margin: 0, fontSize: 19, fontWeight: 600 }}>{cmd.label.replace(/…$/, '')}</h2>
          <p style={{ margin: 0, fontSize: 14, color: T.muted }}>
            Which {noun.toLowerCase()} to bring in{addsDirectly ? ' — every matching line is added.' : '; you choose the lines next.'}
          </p>
        </div>

        <div style={{ padding: '16px 22px', overflow: 'auto', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px 18px' }}>
          {criteria.map(c => {
            const required = !c.protected && blank(c.value) && !c.default_helper
            return (
              <label key={c.name} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>
                  {c.label || c.name}{required && <span style={{ color: '#c62828' }}> *</span>}
                </span>
                {input(c)}
                {!c.protected && c.default_helper && blank(values[c.name]) && (
                  <span style={{ fontSize: 13, color: T.muted }}>Defaults to @{c.default_helper}</span>
                )}
              </label>
            )
          })}
        </div>

        <div style={dialogFoot}>
          {error
            ? <span role="alert" style={{ fontSize: 14, color: '#a61b1b' }}>{error}</span>
            : <span style={{ fontSize: 14, color: T.muted }}>
                {criteria.some(c => c.protected) ? 'Locked values come from this record.' : ''}
              </span>}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onCancel} style={btn}>Cancel</button>
          <button type="button" onClick={submit} style={btnPri}>
            {addsDirectly ? 'Add lines' : 'Show lines'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** "PO PO1 · From line 2" — the criteria a picker's lines came from. */
export function criteriaRecap(criteria: LineCriterion[], values: Record<string, unknown>): string {
  return criteria.map((c) => {
    const k = Object.keys(values).find(x => x.toLowerCase() === c.name.toLowerCase())
    const v = !c.protected && k ? values[k] : c.value
    return blank(v) ? '' : `${c.label || c.name} ${asText(v)}`
  }).filter(Boolean).join(' · ')
}
